import type { ChangeEventHandler, FocusEventHandler } from 'react'
import { useCallback, useRef } from 'react'
import clsx from 'clsx'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { BodyTextMedium } from 'LuxKit/Typography/BodyText'
import { preventDefault } from 'lib/web/eventHandlerUtils'
import { tripPlannerNavigationRowStyle } from './tripPlannerNavigationRowStyle'

const SEARCH_ICON = <LineSearchIcon />
const CLEAR_ICON = <LineTimesIcon />

// The design shows search as a borderless row matching New chat, which LuxKit's bordered TextInput can't be styled into.
const Row = styled.div`
  ${tripPlannerNavigationRowStyle}
  /* New chat's height, kept while the 28px clear button shows. */
  min-block-size: ${rem(40)};
  padding-block: ${rem(6)};

  &.selected {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
  }
`

// Leaves layout to the row; the clear button sits outside because a label may hold no control besides its input.
const SearchField = styled.label`
  display: contents;
  cursor: text;
`

const Input = styled.input`
  ${BodyTextMedium}
  flex: 1;
  min-inline-size: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  outline: none;
  appearance: none;

  &::-webkit-search-cancel-button {
    appearance: none;
  }

  &::placeholder {
    color: inherit;
    opacity: 1;
  }

  &:focus::placeholder {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

interface Props {
  label: string
  value: string
  selected: boolean
  onChange: ChangeEventHandler<HTMLInputElement>
  onClear: () => void
  onFocus: FocusEventHandler<HTMLInputElement>
  onBlur: FocusEventHandler<HTMLInputElement>
}

function TripPlannerSearchChatsRow({ label, value, selected, onChange, onClear, onFocus, onBlur }: Props) {
  const intl = useIntl()
  const inputRef = useRef<HTMLInputElement>(null)

  // Mouse down is cancelled so the input keeps focus; keyboard presses leave it on the unmounting button.
  const handleClear = useCallback(() => {
    onClear()
    inputRef.current?.focus()
  }, [onClear])

  const clearLabel = intl.formatMessage({
    id: '4FciAALTl-TM7L7KUVOG',
    defaultMessage: 'Clear search',
    description: 'Accessible label for the button that empties the chat search input in the TripPlanner side nav and menu drawer',
  })

  return (
    <Row className={clsx({ selected })}>
      <SearchField>
        {SEARCH_ICON}
        <Input
          ref={inputRef}
          type="search"
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={label}
          aria-label={label}
          autoComplete="off"
        />
      </SearchField>
      {!!value && (
        <IconButton
          kind="tertiary"
          size="small"
          horizontalOutdent="end"
          aria-label={clearLabel}
          onMouseDown={preventDefault}
          onClick={handleClear}
        >
          {CLEAR_ICON}
        </IconButton>
      )}
    </Row>
  )
}

export default TripPlannerSearchChatsRow
